import { useEffect } from "react"
import { Link, useNavigate, useParams, useSearchParams, useOutletContext } from "react-router-dom"
import { useComputed, useSignal } from "@preact/signals-react"

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Dropdown from 'react-bootstrap/Dropdown'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import ConfirmationModal from "../../components/ConfirmationModal"
import DebugAccordion from "../../components/DebugAccordion"
import ErrorDetail from "../../components/ErrorDetail.jsx"
import Icon from "../../components/Icon.jsx"
import Loadable from "../../components/Loadable.jsx"
import LoadingAnimation from "../../components/LoadingAnimation"
import PageTitle from "../../components/PageTitle.jsx"
import UtgNav from "./components/UtgNav"
import UtgTools from "./components/UtgTools.jsx"

import useRedirectOnBeIdChange from '../../hooks/useRedirectOnBeIdChange.js'

import { fetchComponentConfig, updateComponentConfig } from '../../state/actions/componentConfigActions.js'
import { fetchComponentConfigSchema } from '../../state/actions/componentConfigSchemaActions'
import { NotificationTypes, addNotification } from "../../state/actions/notificationActions"
import { fetchUtg } from '../../state/actions/utgActions.js'

import { componentConfigSignal, componentConfigLoadedSignal, componentConfigErrorSignal } from '../../state/signals/componentConfigSignals.js'
import { componentConfigSchemaSignal, componentConfigSchemaLoadedSignal, ccSchemaApiInterfaceConfigSignal } from '../../state/signals/componentConfigSchemaSignals'
import { utgSignal, utgLoadedSignal, utgErrorSignal } from '../../state/signals/utgSignals.js'

import { ComponentInterfaceType } from "../../utils/constants"

export default function UtgApiInterfaceList() {

    const navigate = useNavigate()
    const { beId, componentId } = useParams()
    const beIdChanged = useRedirectOnBeIdChange(beId, `/be/${beId}/utgs`)
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewUtgs) {
            addNotification('You are not authorized to view UTGs for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(
        () => {
            return userPermissionsSummary?.value?.canEditUtgs
        }
    )

    useEffect(() => {
        if (beIdChanged) return
        fetchUtg(beId, componentId, config, accessToken).then(
            (utg) => {
                fetchComponentConfigSchema(beId, utgSignal.value.utgVersion, config, accessToken)
                fetchComponentConfig(beId, utgSignal.value.machinePermanentId, utgSignal.value.componentPermanentId, config, accessToken)
            }
        )
    }, [beId, beIdChanged, componentId, config, accessToken])

    const apiInterfaceDropdownItemsComputed = useComputed(() => {
        if (!componentConfigSchemaLoadedSignal.value) {
            //console.log("Component config schema is not loaded")
            return []
        }
        else if (!componentConfigSchemaSignal.value) {
            //console.error("Component config schema is loaded, but has no value")
            return []
        }
        else if (ccSchemaApiInterfaceConfigSignal.value?.properties?.apiInterface?.items) {
            //console.log("Component config schema is loaded and config is defined")
            const apiInterfaceItems = ccSchemaApiInterfaceConfigSignal.value.properties.apiInterface.items
            const apiInterfaceOptions = apiInterfaceItems.map(s => {
                const apiInterfaceName = s.properties.cdType["s4-caption"]
                const id = s.properties.cdType["s4-model-key"]
                return {
                    apiInterfaceName,
                    id
                }
            })
            return apiInterfaceOptions
        }
        //console.log("Component config schema is loaded but config is undefined")
        //console.log(componentConfigSchemaSignal.value)
        return []
    })

    const apiInterfacesComputed = useComputed(() => {
        if (componentConfigSignal.value) {
            // extract apiInterface from componentConfigData for ease of use
            const interfaces = componentConfigSignal.value?.config?.apiInterfaceConfig?.apiInterface ?? []
            return interfaces
        }
        return []
    })

    const showDeleteModalSignal = useSignal(false)
    const confirmCdTypeSignal = useSignal('')
    const confirmTaskDescSignal = useSignal('')
    const confirmListenPortSignal = useSignal('')
    const accordionData = [
        { name: 'API Interfaces', data: apiInterfacesComputed.value },
        { name: 'Component Config', data: componentConfigSignal.value },
        { name: 'API Interfaces Dropdown data', data: apiInterfaceDropdownItemsComputed.value },
        { name: 'UTG Data', data: utgSignal.value },
        { name: 'Component Schema', data: componentConfigSchemaSignal.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config }
    ]

    function ApiInterfaceTableRows({ apiInterfaces }) {
        if (componentConfigErrorSignal.value) {
            const errors = [componentConfigErrorSignal.value]
            return (<tr><td colSpan={4}><ErrorDetail errors={errors} /></td></tr>)
        }
        if (!componentConfigLoadedSignal.value) {
            return <tr><td colSpan={4}><LoadingAnimation /></td></tr>
        }
        if (apiInterfaces && apiInterfaces.length == 0) {
            return (
                <tr><td colSpan="4" className="text-center">This UTG does not have any configured API Interfaces</td></tr>
            )
        }
        let set = new Set()
        let rows = []
        for (let i = 0; i < apiInterfaces.length; i++) {
            const apiInterface = apiInterfaces[i]
            const key = `${apiInterface.cdType}${apiInterface.listenPort}`
            if (set.has(key)) {
                continue
            }
            set.add(key)
            rows.push(<ApiInterfaceRow apiInterface={apiInterface} key={key} />)
        }
        return rows
    }

    function ApiInterfaceRow({ apiInterface }) {
        const cdType = apiInterface.cdType
        const taskDesc = apiInterface.taskDesc
        const listenPort = apiInterface.listenPort
        return (
            <tr>
                <td><Link to={`./${apiInterface.cdType}?port=${apiInterface.listenPort}`}>{apiInterface.taskDesc}</Link></td>
                <td>{apiInterface.cdType}</td>
                <td>{apiInterface.listenPort}</td>
                <td>
                    {userPermissionsSummary?.value?.canEditUtgs && <Link to={`./${apiInterface.cdType}?port=${apiInterface.listenPort}`}>
                        <Button variant="outline-primary" size="sm"><Icon icon="pencil" width="16" height="16"></Icon> Edit</Button>
                    </Link>}
                    {userPermissionsSummary?.value?.canEditUtgs && <Button variant="outline-danger" size="sm" className="mx-1" onClick={() => showModal(cdType, taskDesc, listenPort)}><Icon icon="trash" width="16" height="16"></Icon> Delete</Button>}
                </td>
            </tr>
        )
    }

    const deleteApiInterface = () => {
        showDeleteModalSignal.value = false

        // filter out apiInterface to be deleted
        const filteredApiInterfaces = componentConfigSignal.value.config.apiInterfaceConfig.apiInterface.filter(apiInterface =>
            !(apiInterface.listenPort == confirmListenPortSignal.value));

        // set newly filtered apiInterface array 
        componentConfigSignal.value.config.apiInterfaceConfig.apiInterface = filteredApiInterfaces

        //build data object for updateComponentConfig request
        let data = {
            config: componentConfigSignal.value.config,
            hash: componentConfigSignal.value.hash,
            isCommitted: componentConfigSignal.value.isCommitted,
            lastUpdatedAt: componentConfigSignal.value.lastUpdatedAt,
            lastUpdatedBy: componentConfigSignal.value.lastUpdatedBy,
            revisionId: componentConfigSignal.value.revisionId,
            componentId: componentConfigSignal.value.componentId
        }

        //console.log(data)

        updateComponentConfig(beId, utgSignal.value.machinePermanentId, utgSignal.value.componentPermanentId, data, config, accessToken)
            .then(() => {
                console.log('successfully updated component config')
                addNotification('The Api Interface was successfully removed.', 'success', true)
                fetchComponentConfig(beId, utgSignal.value.machinePermanentId, utgSignal.value.componentPermanentId, config, accessToken)
            })
            .catch(error => {
                console.log(error.toString())
                addNotification(error.toString(), NotificationTypes.ERROR, false)
                return false
            })
            .finally(() => {
                // must return false to avoid postback
                return false
            })
    }

    const showModal = (cdType, taskDesc, listenPort) => {
        // just set the state and the modal will appear
        confirmCdTypeSignal.value = cdType
        confirmTaskDescSignal.value = taskDesc
        confirmListenPortSignal.value = listenPort
        showDeleteModalSignal.value = true
    }

    function NewApiInterfaceDropdown() {
        // only allow one Oracle Interface per UTG
        let oracleApiInterfaces = apiInterfacesComputed.value.filter(apiInterface => {
            return apiInterface.cdType == ComponentInterfaceType.OraclePaymentInterface
        })

        let apiInterfaceOptions = apiInterfaceDropdownItemsComputed.value.map(s => {
            let isDisabled = false

            if (s.id == ComponentInterfaceType.OraclePaymentInterface && oracleApiInterfaces.length) {
                isDisabled = true
            }

            return (
                <Dropdown.Item href={`/be/${beId}/utgs/${componentId}/apiinterfaces/${s.id}`} key={s.id} disabled={isDisabled}>{s.apiInterfaceName}</Dropdown.Item>
            )
        })

        return (
            <DropdownButton id="dropdown-add-apiinterface"
                variant="outline-secondary"
                size="sml"
                title="New API Interface"
                className="mx-3">
                {apiInterfaceOptions}
            </DropdownButton>
        )
    }

    return (
        <>
            {
                userPermissionsSummary?.value?.canEditUtgs && (
                    <ConfirmationModal
                        show={showDeleteModalSignal.value}
                        handleCancel={() => showDeleteModalSignal.value = false}
                        handleConfirm={() => deleteApiInterface()}
                        message={`Are you sure you want to delete ${confirmTaskDescSignal.value}? This action cannot be undone.`}
                        title={`Deleting ${confirmTaskDescSignal.value}`} />
                )
            }
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/utgs` }}>
                    UTG List
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{
                    utgLoadedSignal.value ? <>Manage {utgSignal.value.componentNickname}</> : <>Loading...</>
                }</Breadcrumb.Item>
            </Breadcrumb>
            <Stack direction="horizontal" gap={3}>
                <PageTitle title='UTG API Interfaces' canEditSignal={canEditComputed} />
                <Loadable
                    loadedSignals={[componentConfigLoadedSignal]}
                    errorSignals={[componentConfigErrorSignal]}
                >
                    <UtgTools componentConfigData={componentConfigSignal.value}></UtgTools>
                </Loadable>
            </Stack>
            <UtgNav activeKey="2"></UtgNav>

            <Table responsive striped hover size="sm">
                <thead>
                    <tr>
                        <th>Description</th>
                        <th>Type</th>
                        <th>Port</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody><ApiInterfaceTableRows apiInterfaces={apiInterfacesComputed.value} /></tbody>
            </Table>
            {userPermissionsSummary?.value?.canEditUtgs && <NewApiInterfaceDropdown />}
            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}